import { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';

// A search-as-you-type select — queries the backend as the user types
// instead of loading every record into a giant dropdown. Used for
// Load and Driver selection on the Create Payment form.
//
// fetchResults: async (query) => array of items
// renderLabel: (item) => string shown once selected
// renderOption: (item) => JSX shown in the dropdown list
function SearchSelect({ label, value, onSelect, fetchResults, renderLabel, renderOption, placeholder }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!query) {
      setResults([]);
      return;
    }

    // Debounce so we don't fire a request on every single keystroke
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      const data = await fetchResults(query);
      setResults(data);
      setLoading(false);
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [query, fetchResults]);

  const handleSelect = (item) => {
    onSelect(item);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const handleClear = () => {
    onSelect(null);
    setQuery('');
  };

  return (
    <div style={{ position: 'relative' }}>
      <label style={styles.label}>{label}</label>

      {value ? (
        <div style={styles.selectedBox}>
          <span>{renderLabel(value)}</span>
          <button type="button" onClick={handleClear} style={styles.clearBtn}>
            <X size={14} />
          </button>
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          <Search size={15} style={styles.searchIcon} />
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            style={styles.input}
          />
          {open && query && (
            <div style={styles.dropdown}>
              {loading && <div style={styles.dropdownMessage}>Searching...</div>}
              {!loading && results.length === 0 && (
                <div style={styles.dropdownMessage}>No results found.</div>
              )}
              {!loading &&
                results.map((item, i) => (
                  <div key={i} onClick={() => handleSelect(item)} style={styles.option}>
                    {renderOption(item)}
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 6,
    marginTop: 12,
  },
  input: {
    width: '100%',
    padding: '10px 12px 10px 34px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  },
  searchIcon: {
    position: 'absolute',
    left: 10,
    top: 12,
    color: '#9ca3af',
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    marginTop: 4,
    maxHeight: 220,
    overflowY: 'auto',
    zIndex: 20,
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
  dropdownMessage: {
    padding: '10px 12px',
    fontSize: 13,
    color: '#9ca3af',
  },
  option: {
    padding: '10px 12px',
    fontSize: 13,
    cursor: 'pointer',
    borderBottom: '1px solid #f3f4f6',
  },
  selectedBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #dbeafe',
    backgroundColor: '#eff6ff',
    fontSize: 14,
    color: '#1e40af',
    fontWeight: 600,
  },
  clearBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#1e40af',
    display: 'flex',
  },
};

export default SearchSelect;